import React from "react";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import LibraryBooksOutlinedIcon from "@mui/icons-material/LibraryBooksOutlined";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import logo from "../../assets/img/logo.png";
import {
  canAccessAdminPanel,
  getDefaultAdminPath,
} from "../../config/auth/accessControl";
import { ApplicationState } from "../../store";
import { doLogoutApp } from "../../store/ducks/auth/actions";
import AppFooter from "../AppFooter/AppFooter";
import AppHeader from "../AppHeader/AppHeader";
import AppSidebar, {
  AppSidebarItem,
  AppSidebarSubItem,
} from "../AppSidebar/AppSidebar";
import ChatbotPage from "../../Pages/PortalAluno/Chatbot/ChatbotPage";
import ScrollToTop from "../ScrollToTop/ScrollToTop";
import {
  buildSearchRoute,
  readSearchQueryParam,
} from "../../utils/portalAluno/listingFilters";
import { isSearchable } from "../../services/portalAluno/search/searchService";
import "./styles.css";

const SIDEBAR_TRANSITION_DURATION = 320;

interface ContentLayoutSnapshot {
  left: number;
}

/** Espelha a aba "Menu" do app: header no desktop, sidebar no mobile. */
const userMenuItems: AppSidebarSubItem[] = [
  { label: "Notificações", route: "/app/menu/notificacoes" },
  {
    label: "Enquetes e Pesquisas",
    route: "/app/menu/enquetes",
  },
  { label: "Meus Dados", route: "/app/menu/meus-dados" },
  { label: "Histórico", route: "/app/menu/historico" },
  { label: "FAQ", route: "/app/menu/faq" },
  {
    label: "Fale Conosco",
    route: "/app/menu/fale-conosco",
  },
  { label: "Sair", route: "/exit", action: "logout" },
];

const profileMenuItems = userMenuItems.filter(
  (item) =>
    item.route === "/app/menu/meus-dados" || item.action === "logout"
);

const headerMenuItems = userMenuItems.filter(
  (item) => !profileMenuItems.includes(item)
);

const sidebarItems: AppSidebarItem[] = [
  {
    icon: <ArticleOutlinedIcon />,
    route: "/app/conteudo",
    label: "Home",
    children: [
      { label: "Home", route: "/app" },
      { label: "Destaque", route: "/app/conteudo/destaques" },
      {
        label: "Segurança do trabalho",
        route: "/app/conteudo/seguranca-do-trabalho",
      },
      {
        label: "Treinamentos de 1 ponto",
        route: "/app/conteudo/treinamentos-de-1-ponto",
      },
      {
        label: "Comunicados",
        route: "/app/conteudo/comunicados",
      },
      { label: "Cursos", route: "/app/conteudo/cursos" },
      { label: "Trilhas", route: "/app/conteudo/trilhas" },
    ],
  },
  {
    icon: <span className="app-sidebar__chatbot-icon" />,
    route: "/app/chatbot",
    label: "Chatbot",
  },
  {
    icon: <LibraryBooksOutlinedIcon />,
    route: "/app/biblioteca",
    label: "Biblioteca",
  },
  {
    icon: <StarRoundedIcon />,
    route: "/app/favoritos",
    label: "Favoritos",
  },
  {
    icon: <MenuRoundedIcon />,
    route: "/app/menu",
    label: "Menu",
    // No desktop esses itens vivem no menu do header; aqui so no drawer mobile.
    mobileOnly: true,
    children: userMenuItems,
  },
];

const AppLayout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [isLightMode, setIsLightMode] = React.useState(true);
  const [searchValue, setSearchValue] = React.useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const contentRef = React.useRef<HTMLElement>(null);
  const contentAnimationRef = React.useRef<Animation | null>(null);
  const previousContentLayoutRef = React.useRef<ContentLayoutSnapshot | null>(
    null
  );
  const auth = useSelector(
    (state: ApplicationState) => state.auth.data
  );
  const hasAdminPanelAccess = canAccessAdminPanel(auth);
  const isDetailsPage = location.pathname.includes("/detalhes/");
  const isChatbotPage = location.pathname === "/app/chatbot";
  const isSearchPage = location.pathname === "/app/busca";
  const [hasOpenedChatbot, setHasOpenedChatbot] = React.useState(isChatbotPage);

  React.useEffect(() => {
    if (isChatbotPage) setHasOpenedChatbot(true);
  }, [isChatbotPage]);

  React.useLayoutEffect(() => {
    const content = contentRef.current;
    const previousLayout = previousContentLayoutRef.current;

    previousContentLayoutRef.current = null;

    if (!content || !previousLayout) return;
    if (
      typeof content.animate !== "function" ||
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }

    const nextLayout = content.getBoundingClientRect();
    const deltaX = previousLayout.left - nextLayout.left;

    if (!Number.isFinite(deltaX) || deltaX === 0) return;

    const animation = content.animate(
      [
        {
          transform: `translate3d(${deltaX}px, 0, 0)`,
        },
        {
          transform: "translate3d(0, 0, 0)",
        },
      ],
      {
        duration: SIDEBAR_TRANSITION_DURATION,
        easing: "ease",
        fill: "both",
      }
    );

    contentAnimationRef.current = animation;
    animation.onfinish = () => {
      if (contentAnimationRef.current !== animation) return;

      contentAnimationRef.current = null;
      animation.cancel();
    };
  }, [isSidebarOpen]);

  React.useEffect(
    () => () => {
      contentAnimationRef.current?.cancel();
    },
    []
  );

  React.useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const activeSearchQuery = isSearchPage
    ? readSearchQueryParam(location.search)
    : "";

  React.useEffect(() => {
    setSearchValue(activeSearchQuery);
  }, [activeSearchQuery]);

  React.useEffect(() => {
    if (!isMobileMenuOpen) return;

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsMobileMenuOpen(false);
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isMobileMenuOpen]);

  React.useEffect(() => {
    if (typeof window.matchMedia !== "function") return;

    const desktopMediaQuery = window.matchMedia("(min-width: 790px)");
    const closeMobileMenuOnDesktop = (event: MediaQueryListEvent) => {
      if (event.matches) setIsMobileMenuOpen(false);
    };

    desktopMediaQuery.addEventListener?.("change", closeMobileMenuOnDesktop);

    return () => {
      desktopMediaQuery.removeEventListener?.(
        "change",
        closeMobileMenuOnDesktop
      );
    };
  }, []);

  const handleAdminAccess = () => {
    setIsMobileMenuOpen(false);
    window.open(getDefaultAdminPath(), "_blank", "noopener,noreferrer");
  };

  const handleLogout = () => {
    setIsMobileMenuOpen(false);
    dispatch(doLogoutApp(auth?.accessToken));
    navigate("/exit");
  };

  const handleSearchSubmit = () => {
    setIsMobileMenuOpen(false);

    if (!isSearchable(searchValue)) return;

    navigate(buildSearchRoute(searchValue));
  };

  const captureContentLayout = () => {
    const content = contentRef.current;

    if (!content) return;

    const currentLayout = content.getBoundingClientRect();
    contentAnimationRef.current?.cancel();
    contentAnimationRef.current = null;
    previousContentLayoutRef.current = {
      left: currentLayout.left,
    };
  };

  const handleSidebarToggle = () => {
    captureContentLayout();
    setIsSidebarOpen((current) => !current);
  };

  const handleSidebarOpen = () => {
    if (isSidebarOpen) return;

    captureContentLayout();
    setIsSidebarOpen(true);
  };

  return (
    <div
      className={`app-shell${
        isLightMode ? "" : " app-layout--dark"
      }${isChatbotPage ? " app-shell--chatbot" : ""}`}
    >
      <ScrollToTop />
      <div className="app-layout">
        <AppSidebar
          items={sidebarItems}
          logo={logo}
          isOpen={isSidebarOpen}
          isLightMode={isLightMode}
          isMobileOpen={isMobileMenuOpen}
          searchValue={searchValue}
          showAdminAccess={hasAdminPanelAccess}
          onAdminAccessClick={handleAdminAccess}
          onMobileClose={() => setIsMobileMenuOpen(false)}
          onNavigate={() => setIsMobileMenuOpen(false)}
          onLogout={handleLogout}
          onRequestOpen={handleSidebarOpen}
          onSearchChange={setSearchValue}
          onSearchSubmit={handleSearchSubmit}
          onThemeToggle={() => setIsLightMode((current) => !current)}
        />
        <main ref={contentRef} className="app-layout__content">
          <AppHeader
            isLightMode={isLightMode}
            isSidebarOpen={isSidebarOpen}
            isMobileMenuOpen={isMobileMenuOpen}
            isSticky={!isDetailsPage}
            logo={logo}
            avatarUrl={auth?.imgUsuario}
            menuItems={headerMenuItems}
            profileMenuItems={profileMenuItems}
            searchValue={searchValue}
            showAdminAccess={hasAdminPanelAccess}
            onAdminAccessClick={handleAdminAccess}
            onMenuClick={handleSidebarToggle}
            onLogout={handleLogout}
            onMobileMenuClick={() =>
              setIsMobileMenuOpen((current) => !current)
            }
            onSearchChange={setSearchValue}
            onSearchSubmit={handleSearchSubmit}
            onThemeToggle={() => setIsLightMode((current) => !current)}
          />
          <div className="app-layout__page">
            {hasOpenedChatbot && (
              <div
                className="app-layout__chatbot-session"
                hidden={!isChatbotPage}
              >
                <ChatbotPage />
              </div>
            )}
            <div className="app-layout__outlet" hidden={isChatbotPage}>
              <Outlet />
            </div>
          </div>
        </main>
      </div>
      {!isChatbotPage && <AppFooter />}
    </div>
  );
};

export default AppLayout;
